import { useState } from 'react';
import { useRouter } from '@uirouter/react';
import _ from 'lodash';
import { Wand2 } from 'lucide-react';

import { useAnalytics } from '@/react/hooks/useAnalytics';
import { usePublicSettings } from '@/react/portainer/settings/queries';

import { Button } from '@@/buttons';
import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { FormSection } from '@@/form-components/FormSection';
import { TextTip } from '@@/Tip/TextTip';

import { EnvironmentSelector } from './EnvironmentSelector';
import {
  EnvironmentOptionValue,
  existingEnvironmentTypes,
  newEnvironmentTypes,
  environmentTypes,
} from './environment-types';

export function EnvironmentTypeSelectView() {
  const [types, setTypes] = useState<EnvironmentOptionValue[]>([]);
  const { trackEvent } = useAnalytics();
  const router = useRouter();

  const { data: publicSettings } = usePublicSettings();
  const isFIPSMode = !!publicSettings?.FIPSMode;

  // Modify existing environment types to disable ACI when FIPS mode is enabled
  const modifiedExistingEnvironmentTypes = existingEnvironmentTypes.map(
    (env) => {
      if (env.id === 'aci') {
        return {
          ...env,
          disabled: () => isFIPSMode,
          tooltip: () =>
            isFIPSMode ? 'FIPS mode restricts access to ACI' : '',
        };
      }
      return env;
    }
  );

  return (
    <>
      <PageHeader
        title="Quick Setup"
        breadcrumbs={[{ label: 'Environment Wizard' }]}
        reload
      />

      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetTitle icon={Wand2} title="Environment Wizard" />
            <WidgetBody>
              <div className="form-horizontal">
                <FormSection title="Select your environment(s)">
                  <p className="text-muted small">
                    You can onboard different types of environments, select all
                    that apply.
                  </p>
                  <p className="control-label !mb-2">
                    Connect to existing environments
                  </p>
                  <EnvironmentSelector
                    value={types}
                    onChange={setTypes}
                    options={modifiedExistingEnvironmentTypes}
                  />
                  {isFIPSMode ? (
                    <TextTip color="blue" className="mb-6">
                      Environment creation is not available while FIPS mode is
                      enabled.
                    </TextTip>
                  ) : (
                    <>
                      <p className="control-label !mb-2">
                        Set up new environments
                      </p>
                      <EnvironmentSelector
                        value={types}
                        onChange={setTypes}
                        options={newEnvironmentTypes}
                        hiddenSpacingCount={
                          modifiedExistingEnvironmentTypes.length -
                          newEnvironmentTypes.length
                        }
                      />
                    </>
                  )}
                </FormSection>
              </div>
              <Button
                disabled={types.length === 0}
                data-cy="start-wizard-button"
                onClick={() => startWizard()}
                className="!ml-0"
              >
                Start Wizard
              </Button>
            </WidgetBody>
          </Widget>
        </div>
      </div>
    </>
  );

  function startWizard() {
    if (types.length === 0) {
      return;
    }

    const steps = _.compact(
      types.map((id) => environmentTypes.find((eType) => eType.id === id))
    );

    trackEvent('endpoint-wizard-endpoint-select', {
      category: 'portainer',
      metadata: {
        environment: steps.map((step) => step.label).join('/'),
      },
    });

    router.stateService.go('portainer.wizard.endpoints.create', {
      envType: types,
    });
  }
}
